import React, { type ReactNode, useCallback, useEffect, useState } from 'react';

import useEmblaCarousel, { type EmblaOptionsType } from 'embla-carousel-react';
import classNames from 'classnames';
import './style.scss';
import type { ExtraClassType } from '@components/models';

type NavItemProps = {
    enabled: boolean;
    onClick: () => void;
};

export type NavProps = {
    prevNav: NavItemProps;
    nextNav: NavItemProps;
};

type Props = {
    children: ReactNode;
    extraClassForBase?: ExtraClassType;
    extraClassForContainer?: ExtraClassType;
    extraClassForViewport?: ExtraClassType;
    options?: EmblaOptionsType;
    renderHeaderNode: ReactNode;
    renderNavComponents: React.FC<NavProps>; //pass navigation UI for better customization of carousel arrows
};

export const SlideContainer = (props: any) => {
    return <div className="base-carousel__slide">{props.children}</div>;
};

// Will use embla carousel implementation
const BaseCarousel: React.FC<Props> = (props: Props) => {
    const { options, extraClassForViewport, extraClassForBase, extraClassForContainer } = props;
    const [carouselRef, carouselApi] = useEmblaCarousel(options);
    const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
    const [nextBtnEnabled, setNextBtnEnabled] = useState(false);

    const onClickPrevNav = useCallback(() => carouselApi && carouselApi.scrollPrev(), [carouselApi]);
    const onClickNextNav = useCallback(() => carouselApi && carouselApi.scrollNext(), [carouselApi]);

    const onSelect = useCallback(() => {
        if (!carouselApi) return;
        setPrevBtnEnabled(carouselApi.canScrollPrev());
        setNextBtnEnabled(carouselApi.canScrollNext());
    }, [carouselApi]);

    useEffect(() => {
        if (!carouselApi) return;
        onSelect();

        let carouselInitialized = false;

        carouselApi.on('init', () => {
            carouselInitialized = true;
        });
        carouselApi.on('select', onSelect);
        carouselApi.on('reInit', onSelect);

        /**
         * Note: If carousel is not firing onInit event, run reInit to load the asset files incl. CSS/JS to make the carousel UI functionality work
         */
        if (!carouselInitialized) {
            setTimeout(() => {
                carouselApi.reInit();
            }, 500);
        }
    }, [carouselApi, carouselRef, onSelect]);

    const baseContainerClasses = classNames({
        'base-carousel': !(extraClassForBase && extraClassForBase?.overrideBaseClass),
        [String(extraClassForBase?.classNames)]: extraClassForBase?.classNames,
    });
    const containerClasses = classNames({
        'base-carousel__container': !(extraClassForContainer && extraClassForContainer?.overrideBaseClass),
        [String(extraClassForContainer?.classNames)]: extraClassForContainer?.classNames,
    });
    const viewportClasses = classNames({
        'base-carousel__viewport': !(extraClassForViewport && extraClassForViewport?.overrideBaseClass),
        [String(extraClassForViewport?.classNames)]: extraClassForViewport?.classNames,
    });

    return (
        <div className={baseContainerClasses}>
            {props.renderHeaderNode}

            <div className={viewportClasses} ref={carouselRef}>
                <div className={containerClasses}>{props.children}</div>
            </div>

            {/* BaseCarousel controls the status of nav component props (via embla carousel props) */}
            {props.renderNavComponents({
                prevNav: { enabled: prevBtnEnabled, onClick: onClickPrevNav },
                nextNav: { enabled: nextBtnEnabled, onClick: onClickNextNav },
            })}
        </div>
    );
};

export default BaseCarousel;
